class TransformationGallerySlider {
  constructor(element) {
    this.container = element;
    this.wrapper = element.querySelector('.transformation-gallery__wrapper');
    this.track = element.querySelector('.transformation-gallery__track');
    this.items = element.querySelectorAll('.transformation-gallery__item');
    this.totalBlocks = parseInt(element.dataset.totalBlocks) || 0;
    
    this.maxDesktopItems = 6;
    this.maxMobileItems = 2;
    this.isSliderActive = false;
    
    // Drag functionality properties
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.currentOffset = 0;
    this.animationId = null;
    this.isAnimationPaused = false;
    this.animationSpeed = 0.5; // pixels per frame
    this.lastTime = 0;
    this.dragDirection = null; // 'horizontal', 'vertical', or null
    this.dragThreshold = 10; // pixels to move before determining direction
    
    this.init();
  }
  
  init() {
    this.checkSliderNeed();
    this.addEventListeners();
  }
  
  checkSliderNeed() {
    const isMobile = window.innerWidth <= 768;
    const maxItems = isMobile ? this.maxMobileItems : this.maxDesktopItems;
    
    // Determine if slider should be active
    const needsSlider = this.totalBlocks > maxItems;
    
    if (needsSlider && !this.isSliderActive) {
      this.activateSlider();
    } else if (!needsSlider && this.isSliderActive) {
      this.deactivateSlider();
    }
  }
  
  activateSlider() {
    this.isSliderActive = true;
    this.container.classList.add('slider-active');
    
    // Set animation speed based on device
    const isMobile = window.innerWidth <= 768;
    this.animationSpeed = isMobile ? 0.3 : 0.5; // pixels per frame
    
    // Clone items for infinite loop
    this.createInfiniteLoop();
    
    // Start continuous animation
    setTimeout(() => {
      this.startContinuousAnimation();
    }, 100);
  }
  
  deactivateSlider() {
    this.isSliderActive = false;
    this.container.classList.remove('slider-active');
    
    // Stop continuous animation
    this.stopContinuousAnimation();
    
    // Remove cloned items
    this.removeClonedItems();
  }
  
  createInfiniteLoop() {
    // Remove any existing cloned items first
    this.removeClonedItems();
    
    // Get fresh items list (in case DOM changed)
    this.items = this.container.querySelectorAll('.transformation-gallery__item:not(.cloned-item)');
    
    // Check if total blocks is odd
    const isOddTotal = this.totalBlocks % 2 === 1;
    
    // Create seamless infinite loop structure
    // Structure: [original] + [clones-set1] + [clones-set2]
    // This ensures proper order: 1,2,3,4 -> 1,2,3,4 -> 1,2,3,4 (no reversal)
    
    // Create first set of clones (right side)
    this.items.forEach((item, index) => {
      const clone = item.cloneNode(true);
      clone.classList.add('cloned-item', 'cloned-set1');
      
      if (isOddTotal && item.classList.contains('odd-total')) {
        if (item.classList.contains('align-down')) {
          clone.classList.remove('align-down');
          clone.classList.add('align-up');
        } else if (item.classList.contains('align-up')) {
          clone.classList.remove('align-up');
          clone.classList.add('align-down');
        }
      }
      
      this.track.appendChild(clone);
    });
    
    // Create second set of clones for extra coverage
    this.items.forEach((item, index) => {
      const clone = item.cloneNode(true);
      clone.classList.add('cloned-item', 'cloned-set2');
      
      if (isOddTotal && item.classList.contains('odd-total')) {
        if (item.classList.contains('align-down')) {
          clone.classList.remove('align-down');
          clone.classList.add('align-up');
        } else if (item.classList.contains('align-up')) {
          clone.classList.remove('align-up');
          clone.classList.add('align-down');
        }
      }
      
      this.track.appendChild(clone);
    });
    
    // Start at position 0 (showing original items)
    this.currentOffset = 0;
    this.track.style.transform = `translateX(${this.currentOffset}px)`;
  }

  updateTrackWidth() {
    // Let CSS handle the width naturally - no forced width needed
    // The cloned items will automatically double the track width
  }
  
  removeClonedItems() {
    const clonedItems = this.track.querySelectorAll('.cloned-item');
    clonedItems.forEach(item => item.remove());
  }

  // Continuous animation control methods
  startContinuousAnimation() {
    if (!this.isSliderActive) return;
    
    this.isAnimationPaused = false;
    this.lastTime = performance.now();
    this.animateFrame();
  }

  stopContinuousAnimation() {
    this.isAnimationPaused = true;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  animateFrame() {
    if (!this.isSliderActive || this.isAnimationPaused || this.isDragging) {
      return;
    }

    const currentTime = performance.now();
    const deltaTime = currentTime - this.lastTime;
    
    // Move continuously to the left (negative direction)
    this.currentOffset -= this.animationSpeed * (deltaTime / 16.67); // Normalize to 60fps
    
    // Check if we need to reset position for infinite loop
    this.handleInfiniteLoop();
    
    // Apply transform
    this.track.style.transform = `translateX(${this.currentOffset}px)`;
    
    this.lastTime = currentTime;
    this.animationId = requestAnimationFrame(() => this.animateFrame());
  }

  handleInfiniteLoop() {
    const originalItemsWidth = this.calculateOriginalItemsWidth();
    
    // Handle infinite loop with proper order maintenance
    // Structure: [original][0 to -originalWidth] + [clones-set1][-originalWidth to -2*originalWidth] + [clones-set2][-2*originalWidth to -3*originalWidth]
    
    // When we complete one full cycle (moved one full set width to the left), 
    // jump back to the beginning to create seamless infinite loop
    if (this.currentOffset <= -originalItemsWidth) {
      this.currentOffset += originalItemsWidth;
    }
    
    // If dragged to the right (positive offset), wrap to the end of first clone set
    if (this.currentOffset > 0) {
      this.currentOffset -= originalItemsWidth;
    }
  }

  pauseAnimation() {
    this.isAnimationPaused = true;
  }

  resumeAnimation() {
    if (this.isSliderActive && this.isAnimationPaused && !this.isDragging) {
      this.isAnimationPaused = false;
      this.lastTime = performance.now();
      this.animateFrame();
    }
  }

  // No boundaries needed for infinite bidirectional scroll
  // The infinite loop handling takes care of positioning automatically

  // No need for position normalization with continuous system
  // The infinite loop is handled automatically in handleInfiniteLoop()

  calculateOriginalItemsWidth() {
    // Calculate width of original items only (not including clones)
    let totalWidth = 0;
    this.items.forEach(item => {
      totalWidth += item.offsetWidth;
      // Add gap between items
      const computedStyle = window.getComputedStyle(this.track);
      const gap = parseFloat(computedStyle.gap) || 0;
      totalWidth += gap;
    });
    return totalWidth;
  }

  // Mouse drag functionality methods (Desktop - original behavior)
  startDragMouse(clientX) {
    if (!this.isSliderActive) return;
    
    this.isDragging = true;
    this.startX = clientX;
    this.currentX = clientX;
    
    // Stop continuous animation
    this.stopContinuousAnimation();
    
    // Add dragging class for visual feedback
    this.container.classList.add('is-dragging');
    
    // Prevent text selection during drag
    document.body.style.userSelect = 'none';
  }

  updateDragMouse(clientX) {
    if (!this.isDragging || !this.isSliderActive) return;
    
    this.currentX = clientX;
    const deltaX = this.currentX - this.startX;
    
    // Update current offset directly - no resistance for smooth dragging
    const newOffset = this.currentOffset + deltaX;
    
    // Apply infinite loop logic even during drag to prevent blank spaces
    this.currentOffset = newOffset;
    this.handleInfiniteLoop();
    
    // Apply transform immediately
    this.track.style.transform = `translateX(${this.currentOffset}px)`;
    
    // Update start position to prevent accumulating offset
    this.startX = this.currentX;
  }

  endDragMouse() {
    if (!this.isDragging || !this.isSliderActive) return;
    
    this.isDragging = false;
    
    // Current offset is already updated during drag, no need to recalculate
    
    // Remove dragging class
    this.container.classList.remove('is-dragging');
    
    // Restore text selection
    document.body.style.userSelect = '';
    
    // Resume continuous animation from exact current position
    setTimeout(() => {
      this.resumeAnimation();
    }, 100);
  }

  // Touch drag functionality methods (Mobile - with direction detection)
  startDrag(clientX, clientY) {
    if (!this.isSliderActive) return;
    
    // Don't fully activate dragging yet - wait to determine direction
    this.startX = clientX;
    this.startY = clientY;
    this.currentX = clientX;
    this.currentY = clientY;
    this.dragDirection = null;
    
    // Note: Don't stop animation or set isDragging yet
    // Wait until we determine the drag direction in updateDrag
  }

  updateDrag(clientX, clientY) {
    if (!this.isSliderActive) return;
    
    this.currentX = clientX;
    this.currentY = clientY;
    
    const deltaX = Math.abs(this.currentX - this.startX);
    const deltaY = Math.abs(this.currentY - this.startY);
    
    // Determine drag direction if not yet determined
    if (!this.dragDirection && (deltaX > this.dragThreshold || deltaY > this.dragThreshold)) {
      if (deltaX > deltaY) {
        // Horizontal drag - activate gallery dragging
        this.dragDirection = 'horizontal';
        this.isDragging = true;
        this.stopContinuousAnimation();
        this.container.classList.add('is-dragging');
        document.body.style.userSelect = 'none';
      } else {
        // Vertical drag - allow page scrolling
        this.dragDirection = 'vertical';
        this.isDragging = false;
        return false; // Signal to allow default behavior
      }
    }
    
    // Only update gallery position if horizontal drag is active
    if (this.isDragging && this.dragDirection === 'horizontal') {
      const deltaXMove = this.currentX - this.startX;
      
      // Update current offset directly - no resistance for smooth dragging
      const newOffset = this.currentOffset + deltaXMove;
      
      // Apply infinite loop logic even during drag to prevent blank spaces
      this.currentOffset = newOffset;
      this.handleInfiniteLoop();
      
      // Apply transform immediately
      this.track.style.transform = `translateX(${this.currentOffset}px)`;
      
      // Update start position to prevent accumulating offset
      this.startX = this.currentX;
      this.startY = this.currentY;
      
      return true; // Signal to prevent default behavior
    }
    
    return false; // Allow default behavior for vertical or undetermined
  }

  endDrag() {
    if (!this.isSliderActive) return;
    
    // Only clean up if we were actually dragging horizontally (for touch)
    if (this.isDragging && this.dragDirection === 'horizontal') {
      this.isDragging = false;
      
      // Current offset is already updated during drag, no need to recalculate
      
      // Remove dragging class
      this.container.classList.remove('is-dragging');
      
      // Restore text selection
      document.body.style.userSelect = '';
      
      // Resume continuous animation from exact current position
      setTimeout(() => {
        this.resumeAnimation();
      }, 100);
    }
    
    // Reset drag direction for next interaction
    this.dragDirection = null;
    this.isDragging = false;
  }
  
  handleResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      // Reset current offset on resize to prevent issues
      this.currentOffset = 0;
      this.checkSliderNeed();
      
      // Reset transform if slider is active
      if (this.isSliderActive) {
        this.track.style.transform = 'translateX(0)';
      }
    }, 250);
  }
  
  addEventListeners() {
    // Handle window resize
    window.addEventListener('resize', () => {
      this.handleResize();
    });
    
    // Mouse drag events - only on wrapper area, not testimonial
    this.wrapper.addEventListener('mousedown', (e) => {
      e.preventDefault();
      this.startDragMouse(e.clientX);
    });
    
    document.addEventListener('mousemove', (e) => {
      this.updateDragMouse(e.clientX);
    });
    
    document.addEventListener('mouseup', () => {
      this.endDragMouse();
    });
    
    // Touch drag events - only on wrapper area, not testimonial
    this.wrapper.addEventListener('touchstart', (e) => {
      const touch = e.touches[0];
      this.startDrag(touch.clientX, touch.clientY);
    }, { passive: true });
    
    this.wrapper.addEventListener('touchmove', (e) => {
      if (this.startX !== 0 || this.startY !== 0) {
        const touch = e.touches[0];
        const shouldPrevent = this.updateDrag(touch.clientX, touch.clientY);
        
        // Only prevent default if we determined this is horizontal drag
        if (shouldPrevent && this.dragDirection === 'horizontal') {
          e.preventDefault(); // Prevent scrolling only for horizontal drag
        }
      }
    }, { passive: false });
    
    this.wrapper.addEventListener('touchend', () => {
      this.endDrag();
    });
    
    // Pause animation on hover - only on wrapper area
    this.wrapper.addEventListener('mouseenter', () => {
      if (!this.isDragging && this.isSliderActive) {
        this.pauseAnimation();
      }
    });
    
    this.wrapper.addEventListener('mouseleave', () => {
      if (!this.isDragging && this.isSliderActive) {
        this.resumeAnimation();
      }
    });
    
    // Handle visibility change (pause when tab is not active)
    document.addEventListener('visibilitychange', () => {
      if (this.isSliderActive && !this.isDragging) {
        if (document.hidden) {
          this.pauseAnimation();
        } else {
          this.resumeAnimation();
        }
      }
    });
    
    // Prevent context menu on long press (mobile) - only on wrapper area
    this.wrapper.addEventListener('contextmenu', (e) => {
      if (this.isDragging) {
        e.preventDefault();
      }
    });
  }
  
  destroy() {
    this.deactivateSlider();
    // Remove event listeners if needed
  }
}

// Initialize galleries when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  const galleries = document.querySelectorAll('.transformation-gallery__container');
  
  galleries.forEach(gallery => {
    new TransformationGallerySlider(gallery);
  });
});

// Handle Shopify theme editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const galleries = event.target.querySelectorAll('.transformation-gallery__container');
    galleries.forEach(gallery => {
      new TransformationGallerySlider(gallery);
    });
  });
  
  document.addEventListener('shopify:section:unload', function(event) {
    // Clean up if needed
  });
}